/**
 * Batch Location Creation - Prefix Validation Utilities
 * 
 * Utilidades para validar prefijos personalizados de nomenclatura.
 * 
 * @module PrefixValidatorUtils
 */

import { NomenclaturePrefixes } from '../models/batch-creation-config.model';

/**
 * Resultado de validación de un prefijo
 */
export interface PrefixValidationResult {
  /**
   * Si el prefijo es válido
   */
  isValid: boolean;

  /**
   * Mensajes de error (vacío si es válido)
   */
  errors: string[];
}

/**
 * Resultado de validación de todos los prefijos
 */
export interface PrefixesValidationResult {
  /**
   * Si todos los prefijos son válidos
   */
  isValid: boolean;

  /**
   * Resultados por prefijo
   */
  byPrefix: {
    row: PrefixValidationResult;
    rack: PrefixValidationResult;
    level: PrefixValidationResult;
    position: PrefixValidationResult;
  };

  /**
   * Todos los errores combinados
   */
  allErrors: string[];
}

/**
 * Reglas de validación para prefijos
 */
export const PREFIX_VALIDATION_RULES = {
  /**
   * Longitud mínima (0 = vacío permitido)
   */
  MIN_LENGTH: 0,

  /**
   * Longitud máxima
   */
  MAX_LENGTH: 10,

  /**
   * Patrón regex: letras, números y guiones
   */
  PATTERN: /^[a-zA-Z0-9-]*$/,

  /**
   * Caracteres permitidos (descripción)
   */
  ALLOWED_CHARS: 'letras, números y guiones (-)',
} as const;

/**
 * Valida un prefijo individual
 * 
 * @param prefix - Prefijo a validar
 * @param fieldName - Nombre del campo (para mensajes)
 * @returns Resultado de validación
 */
export function validatePrefix(
  prefix: string | undefined,
  fieldName: string
): PrefixValidationResult {
  const errors: string[] = [];

  // Vacío es válido (usa default)
  if (prefix === undefined || prefix === '') {
    return { isValid: true, errors: [] };
  }

  // Validar longitud mínima/máxima
  if (prefix.length < PREFIX_VALIDATION_RULES.MIN_LENGTH) {
    errors.push(
      `El prefijo de ${fieldName} debe tener al menos ${PREFIX_VALIDATION_RULES.MIN_LENGTH} caracteres`
    );
  }

  if (prefix.length > PREFIX_VALIDATION_RULES.MAX_LENGTH) {
    errors.push(
      `El prefijo de ${fieldName} no puede exceder ${PREFIX_VALIDATION_RULES.MAX_LENGTH} caracteres (tiene ${prefix.length})`
    );
  }

  // Validar caracteres permitidos
  if (!PREFIX_VALIDATION_RULES.PATTERN.test(prefix)) {
    errors.push(
      `El prefijo de ${fieldName} solo puede contener ${PREFIX_VALIDATION_RULES.ALLOWED_CHARS}`
    );
  }

  // Validación adicional: no puede contener solo guiones
  if (prefix.length > 0 && /^-+$/.test(prefix)) {
    errors.push(
      `El prefijo de ${fieldName} no puede contener solo guiones`
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Valida todos los prefijos de una configuración
 * 
 * @param prefixes - Prefijos a validar
 * @returns Resultado de validación completo
 */
export function validatePrefixes(
  prefixes?: NomenclaturePrefixes
): PrefixesValidationResult {
  const rowResult = validatePrefix(prefixes?.row, 'fila');
  const rackResult = validatePrefix(prefixes?.rack, 'rack');
  const levelResult = validatePrefix(prefixes?.level, 'nivel');
  const positionResult = validatePrefix(prefixes?.position, 'posición');

  const allErrors = [
    ...rowResult.errors,
    ...rackResult.errors,
    ...levelResult.errors,
    ...positionResult.errors,
  ];

  return {
    isValid: allErrors.length === 0,
    byPrefix: {
      row: rowResult,
      rack: rackResult,
      level: levelResult,
      position: positionResult,
    },
    allErrors,
  };
}

/**
 * Verifica si un prefijo contiene caracteres especiales peligrosos
 * (que podrían causar problemas en URLs o nombres de archivo)
 * 
 * @param prefix - Prefijo a verificar
 * @returns true si contiene caracteres peligrosos
 */
export function hasDangerousCharacters(prefix: string | undefined): boolean {
  if (!prefix) return false;

  // eslint-disable-next-line no-control-regex
  const dangerousChars = /[<>:"/\\|?*\x00-\x1F]/;
  return dangerousChars.test(prefix);
}

/**
 * Sanitiza un prefijo eliminando caracteres no permitidos
 * 
 * @param prefix - Prefijo a sanitizar
 * @returns Prefijo sanitizado
 */
export function sanitizePrefix(prefix: string): string {
  // Eliminar caracteres no permitidos
  let sanitized = prefix.replace(/[^a-zA-Z0-9-]/g, '');

  // Truncar si excede longitud máxima
  if (sanitized.length > PREFIX_VALIDATION_RULES.MAX_LENGTH) {
    sanitized = sanitized.substring(0, PREFIX_VALIDATION_RULES.MAX_LENGTH);
  }

  return sanitized;
}

/**
 * Verifica si dos prefijos son idénticos (ignora case)
 * 
 * @param prefix1 - Primer prefijo
 * @param prefix2 - Segundo prefijo
 * @returns true si son idénticos
 */
export function arePrefixesIdentical(
  prefix1: string | undefined,
  prefix2: string | undefined
): boolean {
  const p1 = (prefix1 || '').toLowerCase();
  const p2 = (prefix2 || '').toLowerCase();
  return p1 === p2;
}

/**
 * Detecta prefijos duplicados en la configuración
 * (Puede causar ambigüedad en nomenclaturas)
 * 
 * @param prefixes - Prefijos a verificar
 * @returns Array de mensajes de advertencia
 */
export function detectDuplicatePrefixes(
  prefixes?: NomenclaturePrefixes
): string[] {
  const warnings: string[] = [];

  if (!prefixes) return warnings;

  const { row, rack, level, position } = prefixes;

  // Normalizar a lowercase para comparación
  const normalized = {
    row: (row || '').toLowerCase(),
    rack: (rack || '').toLowerCase(),
    level: (level || '').toLowerCase(),
    position: (position || '').toLowerCase(),
  };

  // Verificar duplicados (solo si no están vacíos)
  if (normalized.row && normalized.row === normalized.rack) {
    warnings.push('Los prefijos de fila y rack son idénticos. Esto puede causar ambigüedad.');
  }

  if (normalized.row && normalized.row === normalized.level) {
    warnings.push('Los prefijos de fila y nivel son idénticos. Esto puede causar ambigüedad.');
  }

  if (normalized.row && normalized.row === normalized.position) {
    warnings.push('Los prefijos de fila y posición son idénticos. Esto puede causar ambigüedad.');
  }

  if (normalized.rack && normalized.rack === normalized.level) {
    warnings.push('Los prefijos de rack y nivel son idénticos. Esto puede causar ambigüedad.');
  }

  if (normalized.rack && normalized.rack === normalized.position) {
    warnings.push('Los prefijos de rack y posición son idénticos. Esto puede causar ambigüedad.');
  }

  if (normalized.level && normalized.level === normalized.position) {
    warnings.push('Los prefijos de nivel y posición son idénticos. Esto puede causar ambigüedad.');
  }

  return warnings;
}

/**
 * Obtiene sugerencias de prefijos alternativos
 * 
 * @param invalidPrefix - Prefijo inválido
 * @returns Array de sugerencias
 */
export function getSuggestedPrefixes(invalidPrefix: string): string[] {
  const suggestions: string[] = [];

  // Sugerencia 1: Sanitizar
  const sanitized = sanitizePrefix(invalidPrefix);
  if (sanitized && sanitized !== invalidPrefix) {
    suggestions.push(sanitized);
  }

  // Sugerencia 2: Primera letra en mayúscula
  if (invalidPrefix.length > 0) {
    const firstLetter = invalidPrefix.charAt(0).toUpperCase();
    if (PREFIX_VALIDATION_RULES.PATTERN.test(firstLetter)) {
      suggestions.push(firstLetter);
    }
  }

  // Sugerencia 3: Prefijos comunes
  const commonPrefixes = ['F', 'R', 'L', 'P', 'ROW', 'RACK', 'NIV', 'POS'];
  suggestions.push(...commonPrefixes);

  // Eliminar duplicados
  return [...new Set(suggestions)];
}

/**
 * Genera un ejemplo de nomenclatura con los prefijos dados
 * 
 * @param prefixes - Prefijos a usar
 * @param depth - Profundidad
 * @returns Ejemplo de nomenclatura
 */
export function generateNomenclatureExample(
  prefixes: Required<NomenclaturePrefixes>,
  depth: 1 | 2 | 3 | 4
): string {
  const { row, rack, level, position } = prefixes;

  switch (depth) {
    case 1:
      return `S1-${position}1`;
    case 2:
      return `S1-${row}1-${position}1`;
    case 3:
      return `S1-${row}1-${rack}1-${position}1`;
    case 4:
      return `S1-${row}1-${rack}1-${level}1-${position}1`;
    default:
      return '';
  }
}

/**
 * Estima la longitud máxima de nomenclatura
 * Útil para validar que no exceda límites de base de datos
 * 
 * @param parentName - Nombre del salón padre
 * @param prefixes - Prefijos configurados
 * @param depth - Profundidad
 * @param maxConsecutive - Consecutivo máximo (ej: 999)
 * @returns Longitud estimada
 */
export function estimateMaxNomenclatureLength(
  parentName: string,
  prefixes: Required<NomenclaturePrefixes>,
  depth: 1 | 2 | 3 | 4,
  maxConsecutive = 999
): number {
  const { row, rack, level, position } = prefixes;
  const consecutiveLength = maxConsecutive.toString().length;

  let length = parentName.length; // Nombre del salón

  switch (depth) {
    case 1:
      length += 1 + position.length + consecutiveLength; // -P999
      break;
    case 2:
      length += 1 + row.length + consecutiveLength; // -F999
      length += 1 + position.length + consecutiveLength; // -P999
      break;
    case 3:
      length += 1 + row.length + consecutiveLength; // -F999
      length += 1 + rack.length + consecutiveLength; // -R999
      length += 1 + position.length + consecutiveLength; // -P999
      break;
    case 4:
      length += 1 + row.length + consecutiveLength; // -F999
      length += 1 + rack.length + consecutiveLength; // -R999
      length += 1 + level.length + consecutiveLength; // -L999
      length += 1 + position.length + consecutiveLength; // -P999
      break;
  }

  return length;
}

/**
 * Verifica si la longitud de nomenclatura excede un límite
 * 
 * @param parentName - Nombre del salón
 * @param prefixes - Prefijos configurados
 * @param depth - Profundidad
 * @param limit - Límite máximo (default: 255 caracteres)
 * @returns true si excede el límite
 */
export function exceedsNomenclatureLengthLimit(
  parentName: string,
  prefixes: Required<NomenclaturePrefixes>,
  depth: 1 | 2 | 3 | 4,
  limit = 255
): boolean {
  const maxLength = estimateMaxNomenclatureLength(parentName, prefixes, depth);
  return maxLength > limit;
}
